// External Packages
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { BsArrowRightSquare, BsFilter } from "react-icons/bs";
import { BsArrowLeftSquare } from "react-icons/bs";
import { RiSteeringFill } from "react-icons/ri";
import { FiDownload } from "react-icons/fi";
import { BsPlusLg } from "react-icons/bs";
import { MdRefresh } from "react-icons/md";
import { BiWorld } from "react-icons/bi";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { AuthContext } from "../../context/auth-context";
import { fetchSmartcarAuth } from "../../api/fetch";
import { arrayToCsv } from "../../util/arrayToCsv";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import SearchBar from "../UI/SearchBar";
import VehicleCard from "./VehicleCard";
import Heading from "../UI/Heading";
import FilterMenu from "../UI/FilterMenu";

export default function VehiclesPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { vehicles, loadingData, fetchVehicles } = useContext(DataContext);

  // Initialize States
  const [filteredVehicles, setFilteredVehicles] = useState(vehicles);

  // ========================================================== SEACH VEHICLES
  const searchVehicle = (query) => {
    const q = query.toLowerCase();
    setFilteredVehicles(
      vehicles.filter((vehicle) =>
        `${vehicle.id} ${vehicle.vin} ${vehicle.make} ${vehicle.model} ${vehicle.year}`
          .toLowerCase()
          .includes(q)
      )
    );
  };

  // ========================================================== FILTER HANDLER
  const filterOptions = [
    { label: "Location", options: ["All", "Boston, MA", "Dallas, TX"] },
    { label: "Booking", options: ["All", "Booked", "Not Booked"] },
    { label: "Available", options: ["All", "Available", "Not Available"] },
  ];
  const filterHandler = (filters) => {
    const filtered = [];
    vehicles.forEach((vehicle) => {
      let filter = true;
      if (filters.location !== "All" && vehicle.location !== filters.location)
        filter = false;
      if (
        filters.booking === "Booked" &&
        (!vehicle.bookings || vehicle.bookings.length === 0)
      )
        filter = false;

      if (
        filters.booking === "Not Booked" &&
        vehicle.bookings &&
        vehicle.bookings.length > 0
      )
        filter = false;

      if (filters.available === "Available" && !vehicle.status.available)
        filter = false;

      if (filters.available === "Not Available" && vehicle.status.available)
        filter = false;

      if (filter) filtered.push(vehicle);
    });
    setFilteredVehicles(filtered);
  };

  const refreshHandler = () => {
    fetchVehicles();
  };

  // ========================================================== DOWNLOAD CSV
  const vinDownloader = () => {
    const formatted = vehicles.map((vehicle) => {
      return {
        id: vehicle.id,
        vin: vehicle.vin,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
      };
    });
    arrayToCsv(formatted, "vehicles");
  };

  // ========================================================== SET UP SMARTCAR
  const setUpSmartcar = async () => {
    try {
      const response = await fetchSmartcarAuth(user);
      window.open(response, "_blank");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="container grid">
      <Heading title="Vehicles">
        <FilterMenu onSubmit={filterHandler} options={filterOptions}>
          <BsFilter className="text-2xl text-white cursor-pointer hover:text-stone-400" />
        </FilterMenu>
        <BsPlusLg
          onClick={() => navigate("/vehicles/add")}
          className="text-lg text-white cursor-pointer hover:text-stone-400"
          title="Add Vehicle"
        />
        <BiWorld
          onClick={() => navigate("/brands")}
          className="text-2xl text-white cursor-pointer hover:text-stone-400"
          title="Add Brand"
        />
        <RiSteeringFill
          onClick={setUpSmartcar}
          className="text-2xl text-white cursor-pointer hover:text-stone-400"
          title="Set up Smartcar"
        />
        <FiDownload
          onClick={vinDownloader}
          className="text-2xl text-white cursor-pointer hover:text-stone-400"
          title="Download VINs"
        />
        <MdRefresh
          onClick={refreshHandler}
          className="text-2xl text-white cursor-pointer hover:text-stone-400"
          title="Refresh Data"
        />
      </Heading>

      <SearchBar
        searchHandler={searchVehicle}
        clearSearch={() => setFilteredVehicles(vehicles)}
      />

      <div className="list">
        {loadingData ? (
          <LoadingSpinnerWrapper />
        ) : (
          <div className="grid gap-8">
            {filteredVehicles.map((vehicle) => (
              <VehicleCard vehicle={vehicle} key={vehicle.id} />
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end space-x-4 text-sm text-stone-200">
        <p className="text-stone-400">
          Showing {filteredVehicles.length} of {filteredVehicles.length}{" "}
        </p>
        <BsArrowLeftSquare className="inline-block text-xl" />
        <BsArrowRightSquare className="inline-block text-xl" />
      </div>
    </div>
  );
}
